import React from 'react';
import { Flex, Box, Text, IconButton, Image, Icon, Spinner } from '@chakra-ui/react';
import { useHlsPlayer } from '../../hooks/useHlsPlayer';
import type { LibraryTrackDto } from '../../api/libraryApi';

interface FloatingAudioPlayerProps {
  track: LibraryTrackDto;
  streamUrl: string;
  sasToken: string | null;
  onClose: () => void;
}

export function FloatingAudioPlayer({ track, streamUrl, sasToken, onClose }: FloatingAudioPlayerProps) {
  const {
    videoRef,
    isPlaying,
    currentTime,
    duration,
    isLoading,
    error,
    toggle,
    seek,
  } = useHlsPlayer({ streamUrl, sasToken });

  // Auto-play when stream is ready
  React.useEffect(() => {
    const videoEl = videoRef.current;
    if (videoEl && !isLoading && streamUrl) {
      videoEl.play().catch(() => { /* browser may block autoplay */ });
    }
  }, [videoRef, isLoading, streamUrl]);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Flex
      direction="column"
      position="fixed"
      bottom={6}
      right={6}
      w="380px"
      bg="white"
      borderRadius="16px"
      boxShadow="0 8px 32px rgba(0,0,0,0.12)"
      border="1px solid"
      borderColor="gray.100"
      overflow="hidden"
      zIndex="popover"
      data-testid="floating-audio-player"
    >
      {/* Hidden video element */}
      <video
        ref={videoRef as React.RefObject<HTMLVideoElement>}
        style={{ display: 'none' }}
        preload="metadata"
      />

      {/* Top row: thumbnail, info, controls */}
      <Flex align="center" gap={3} px={4} py={3}>
        <Box w="36px" h="36px" flexShrink={0} borderRadius="6px" overflow="hidden" bg="gray.100">
          {track.thumbnailUrl ? (
            <Image src={track.thumbnailUrl} alt="" w="100%" h="100%" objectFit="cover" />
          ) : (
            <Box w="100%" h="100%" bg="gray.200" />
          )}
        </Box>

        {/* Track info */}
        <Box flex="0 0 auto" maxW="180px">
          <Text fontSize="sm" fontWeight="600" color="gray.700" noOfLines={1}>{track.title}</Text>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>{track.artist}</Text>
        </Box>

        {/* Play/pause */}
        <IconButton
          aria-label={isPlaying ? 'Pause' : 'Play'}
          size="sm"
          variant="ghost"
          borderRadius="full"
          onClick={toggle}
          isDisabled={isLoading}
          data-testid="floating-player-toggle"
          icon={isLoading ? <Spinner size="sm" /> : (
            <Icon viewBox="0 0 24 24" w={5} h={5}>
              {isPlaying
                ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" fill="currentColor" />
                : <path d="M8 5v14l11-7L8 5z" fill="currentColor" />}
            </Icon>
          )}
        />

        <Box flex={1} />

        {/* Close */}
        <IconButton
          aria-label="Close player"
          size="xs"
          variant="ghost"
          color="gray.400"
          _hover={{ color: 'gray.600' }}
          borderRadius="full"
          onClick={onClose}
          data-testid="floating-player-close"
          icon={
            <Icon viewBox="0 0 16 16" w={3} h={3}>
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </Icon>
          }
        />
      </Flex>

      {/* Bottom row: progress + time */}
      <Flex align="center" gap={2} px={4} pb={3}>
        <Box
          flex={1}
          h="4px"
          bg="gray.200"
          borderRadius="full"
          overflow="hidden"
          cursor="pointer"
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            if (!duration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            seek(percent * duration);
          }}
          data-testid="floating-player-progress"
        >
          <Box h="100%" w={`${progressPercent}%`} bg="brand.500" borderRadius="full" transition="width 0.3s linear" />
        </Box>

        {/* Time */}
        <Text fontSize="xs" color="gray.500" minW="65px" textAlign="right">
          {formatTime(currentTime)} / {formatTime(duration)}
        </Text>
      </Flex>

      {error && (
        <Text fontSize="xs" color="red.500" px={4} pb={2}>
          {error}
        </Text>
      )}
    </Flex>
  );
}

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
